/**
 * Starter category sets offered to a shop that has none yet.
 *
 * Why this exists: `products.category_id` is nullable in the DB, but the
 * product form REQUIRES a category — so a brand-new shop literally cannot add
 * its first product until it invents a category tree. The client registered on
 * 2026-09-01 still had 2 categories and 2 products a day later. This removes
 * that wall.
 *
 * These are ordinary categories once created — the owner renames, deletes,
 * re-parents and adds to them freely. Nothing here is locked or special, and
 * nothing references a template afterwards.
 *
 * Two levels, because that is how shopkeepers actually describe their stock
 * ("სასმელები → ბორჯომი"), and because the cashier finds an item in two taps.
 * Sub-categories are brands where a brand is how the shelf is organised, and
 * product types where it isn't.
 *
 * The Georgian below is the SOURCE. Every word is also in the dictionary
 * (lib/i18n/{ka,ru}/templates.ts, pinned equal by a test), because a template is
 * shown — and its categories CREATED — in the device's language: a category
 * name is shop data, and the owner's rule for colours holds here too („ყველა
 * ენაზე საკუთარი ენით ჩაიწეროს", 2026-09-29). See `templateIn`.
 */

import { templates as kaWords } from './i18n/ka/templates'
import type { Key, T } from './i18n'
import type { ProductUnit } from './mock-data'

export type CategoryTemplate = {
  key: string
  /** Shown on the picker card. */
  name: string
  hint: string
  emoji: string
  groups: { name: string; children?: string[] }[]
}

export const CATEGORY_TEMPLATES: CategoryTemplate[] = [
  {
    key: 'grocery',
    name: 'უბნის მაღაზია / სასურსათო',
    hint: 'სასმელი, სურსათი, რძის ნაწარმი, საყოფაცხოვრებო',
    emoji: '🏪',
    groups: [
      { name: 'გაზიანი სასმელები', children: ['ბორჯომი', 'ნაბეღლავი', 'ლიკანი', 'საირმე', 'კოკა-კოლა', 'ფანტა'] },
      { name: 'წვენი და წყალი', children: ['წვენი', 'სასმელი წყალი', 'მინერალური წყალი'] },
      { name: 'ენერგეტიკული სასმელები' },
      { name: 'ალკოჰოლური სასმელები', children: ['ლუდი', 'ღვინო', 'არაყი', 'კონიაკი'] },
      { name: 'სიგარეტი', children: ['WINSTON', 'MARLBORO', 'PARLIAMENT', 'CAMEL', 'KENT'] },
      { name: 'სურსათი', children: ['ძეხვეული', 'პური', 'პელმენი', 'ხინკალი', 'კუპატი', 'კონსერვი'] },
      { name: 'რძის ნაწარმი', children: ['მაწონი', 'რძე', 'არაჟანი', 'ხაჭო', 'ყველი', 'კარაქი'] },
      { name: 'ნაყინი' },
      { name: 'ტკბილეული', children: ['შოკოლადი', 'ორცხობილა', 'საღეჭი რეზინი'] },
      { name: 'ჩიფსი და კრეკერი' },
      { name: 'ყავა და ჩაი', children: ['ხსნადი ყავა', 'ჩაი', 'ცხელი ყავა'] },
      { name: 'საყოფაცხოვრებო', children: ['ქიმია', 'ერთჯერადი ჭურჭელი', 'პარკები'] },
      { name: 'თავის მოვლა' },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    // 2026-09-26 (step 3b): a shop that sells by WEIGHT. The one template whose
    // profile turns something on — the scale — because nothing on its shelves
    // is sold without it. Meat by the animal and the cut, produce by what the
    // customer asks for.
    key: 'produce',
    name: 'ხორცი / ხილი-ბოსტნეული',
    hint: 'ხორცი, ფრინველი, ხილი, ბოსტნეული, მწვანილი — წონით',
    emoji: '🥩',
    groups: [
      { name: 'ხორცი', children: ['საქონლის', 'ღორის', 'ხბოს', 'ცხვრის', 'ფარში'] },
      { name: 'ფრინველი', children: ['ქათამი', 'ინდაური', 'ფრთა და ბარკალი'] },
      { name: 'ხილი', children: ['ვაშლი', 'ბანანი', 'ციტრუსი', 'ყურძენი', 'სეზონური'] },
      { name: 'ბოსტნეული', children: ['კარტოფილი', 'პომიდორი', 'კიტრი', 'ხახვი და ნიორი', 'კომბოსტო', 'სეზონური'] },
      { name: 'მწვანილი' },
      { name: 'თხილეული და ჩირი' },
      { name: 'კვერცხი' },
      { name: 'ყველი და რძის ნაწარმი', children: ['იმერული', 'სულგუნი', 'ნადუღი', 'მაწონი'] },
      { name: 'სანელებელი' },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    // 2026-10-14: a shop that sells chicken by the cut and eggs by the piece,
    // weighing on a MECHANICAL scale — the owner's friend, and „other shops
    // like it". No electronic scale is switched on; the till asks „რამდენი?"
    // (1.40) and a new product starts in kg (the profile's `unit`).
    key: 'poultry',
    name: 'ქათმის ნაწარმი',
    hint: 'ქათამი, ფილე, ფრთა, გულ-ღვიძლი, ინდაური, კვერცხი — წონით',
    emoji: '🍗',
    groups: [
      { name: 'ქათამი (მთლიანი)' },
      { name: 'ფილე' },
      { name: 'მკერდი' },
      { name: 'ფრთა' },
      { name: 'ბარკალი და ფეხი' },
      { name: 'გულ-ღვიძლი და კუჭი' },
      { name: 'ფარში' },
      { name: 'ინდაური' },
      { name: 'ნახევარფაბრიკატი' },
      { name: 'კვერცხი' },
      { name: 'სანელებელი და სოუსი' },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    key: 'kiosk',
    name: '24 საათიანი ბუთიკი / drive',
    hint: 'სასმელი, სიგარეტი, ცხელი ყავა, სწრაფი წასახემსებელი',
    emoji: '🌙',
    groups: [
      { name: 'გაზიანი სასმელები', children: ['ბორჯომი', 'ნაბეღლავი', 'ლიკანი', 'საირმე', 'კოკა-კოლა', 'ფანტა'] },
      { name: 'წვენი და წყალი', children: ['წვენი', 'სასმელი წყალი'] },
      { name: 'ენერგეტიკული სასმელები' },
      { name: 'ალკოჰოლური სასმელები', children: ['ლუდი', 'ღვინო', 'არაყი'] },
      { name: 'სიგარეტი', children: ['WINSTON', 'MARLBORO', 'PARLIAMENT', 'CAMEL', 'KENT'] },
      { name: 'ცხელი ყავა', children: ['ხსნადი ყავა', 'ჩაი'] },
      // The cup and the lid are stocked products — see the combo add-ons
      // feature, which offers them automatically when a coffee is sold.
      { name: 'ერთჯერადი', children: ['ჭიქა', 'თავსახური', 'ჩხირი'] },
      { name: 'ტკბილეული', children: ['შოკოლადი', 'ორცხობილა', 'საღეჭი რეზინი'] },
      { name: 'ჩიფსი და კრეკერი' },
      { name: 'წასახემსებელი' },
      { name: 'თავის მოვლა' },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    // A shop that sells ONLY household goods was joining (2026-09-13) and
    // the closest thing on offer was the grocery template, where all of
    // this is one group of three. A shopkeeper handed that would have
    // built the rest as one-product categories — exactly what SAMGORI2026
    // did with 92 of them. Shelves here are TYPES, never brands: a
    // household shop is walked by „what is it for", not „who made it".
    key: 'household',
    name: 'საყოფაცხოვრებო ნივთები',
    hint: 'ქიმია, დასუფთავება, ჭურჭელი, აბაზანა, ტექსტილი',
    emoji: '🧹',
    groups: [
      { name: 'სარეცხი საშუალებები', children: ['სარეცხი ფხვნილი', 'სარეცხი გელი', 'კონდიციონერი', 'მათეთრებელი', 'ლაქის ამომყვანი'] },
      { name: 'დასუფთავება', children: ['იატაკის საწმენდი', 'სამზარეულოს საწმენდი', 'სანტექნიკის საწმენდი', 'მინის საწმენდი', 'უნივერსალური'] },
      { name: 'ჭურჭლის რეცხვა', children: ['ჭურჭლის სითხე', 'ღრუბელი და ტილო', 'ჭურჭლის მანქანის ტაბლეტი'] },
      { name: 'დასუფთავების ინვენტარი', children: ['ცოცხი და აქანდაზი', 'ჩოგანი', 'ვედრო', 'ხელთათმანი', 'მტვრის ტილო'] },
      { name: 'ჭურჭელი', children: ['თეფში', 'ჭიქა და ფინჯანი', 'ქვაბი', 'ტაფა', 'დანა-ჩანგალი', 'კონტეინერი'] },
      { name: 'სამზარეულოს წვრილმანი', children: ['საჭრელი დაფა', 'სახეხი და საწური', 'ფოლგა და ცელოფანი', 'თერმოსი', 'ჩაიდანი'] },
      { name: 'აბაზანა და ტუალეტი', children: ['ტუალეტის ქაღალდი', 'საპონი', 'ტუალეტის საწმენდი', 'ჰაერის არომატიზატორი', 'აბაზანის აქსესუარები'] },
      { name: 'პარკები და ერთჯერადი', children: ['ნაგვის პარკი', 'ერთჯერადი ჭურჭელი', 'ხელსახოცი', 'საკვების პარკი'] },
      { name: 'ტექსტილი', children: ['პირსახოცი', 'ზეწარი და ბალიშისპირი', 'საფენი', 'ფარდა', 'მაგიდის გადასაფარებელი'] },
      { name: 'ელექტრო წვრილმანი', children: ['ნათურა', 'ბატარეა', 'დამაგრძელებელი', 'სანთელი და სანთებელა'] },
      { name: 'სახლის მოწყობა', children: ['საკიდი', 'შესანახი ყუთი', 'კალათა', 'სარკე', 'საათი'] },
      { name: 'მავნებლების წინააღმდეგ', children: ['მწერების საწინააღმდეგო', 'თაგვის ხაფანგი', 'ჩრჩილის საწინააღმდეგო'] },
      { name: 'ბაღი და სეზონური', children: ['ყვავილის ქოთანი', 'სარწყავი', 'საახალწლო', 'ზაფხულის'] },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    // For the car-parts stall at a parts market (ავტობაზრობა), where every
    // neighbour sells the same systems and a buyer asks by SYSTEM and PART
    // („სამუხრუჭე — ხუნდები", „საკიდარი — ბურთულა სახსარი"), never by brand: the
    // brand is decided at the counter, from what fits the car. Words are the
    // market's own (ხუნდები, გრანატა, სალენტი), not the textbook's, because
    // that is what the seller will type into the search. Used parts get one
    // shelf of their own: at a bazaar they are large assemblies (a door, an
    // engine) and are walked separately from the new shelves. Owner's friend
    // with a new-and-used parts stall, 2026-09-14.
    key: 'autoparts',
    name: 'ავტონაწილები',
    hint: 'ზეთი, ფილტრი, ხუნდები, საკიდარი, ელექტრო, ძარა, მეორადი',
    emoji: '🔧',
    groups: [
      { name: 'ზეთი და სითხე', children: ['ძრავის ზეთი', 'კოლოფის ზეთი', 'ანტიფრიზი', 'სამუხრუჭე სითხე', 'ჰიდრავლიკის ზეთი', 'მინის სარეცხი სითხე', 'დანამატი'] },
      { name: 'ფილტრი', children: ['ზეთის ფილტრი', 'ჰაერის ფილტრი', 'საწვავის ფილტრი', 'სალონის ფილტრი'] },
      { name: 'სამუხრუჭე სისტემა', children: ['ხუნდები', 'სამუხრუჭე დისკი', 'სამუხრუჭე დოლი', 'სუპორტი', 'სამუხრუჭე მილი', 'ABS სენსორი'] },
      { name: 'საკიდარი', children: ['ამორტიზატორი', 'ზამბარა', 'ბერკეტი', 'ბურთულა სახსარი', 'სტაბილიზატორის ჯოხი', 'ამორტიზატორის საყრდენი', 'სალენტი', 'ბორბლის საკისარი'] },
      { name: 'საჭე და ამძრავი', children: ['საჭის წვერი', 'საჭის მექანიზმი', 'საჭის ტუმბო', 'გრანატა', 'ნახევარღერძი', 'კარდანი'] },
      { name: 'ძრავი', children: ['ღვედი', 'ღვედის დამჭიმი', 'გამანაწილებელი ღვედი / ჯაჭვი', 'წყლის ტუმბო', 'თერმოსტატი', 'სანთელი', 'ანთების კოჭა', 'შუასადები', 'ძრავის ბალიში'] },
      { name: 'გაგრილება და კონდიციონერი', children: ['რადიატორი', 'ვენტილატორი', 'ღუმელის რადიატორი', 'კონდიციონერის კომპრესორი', 'მილი და შლანგი'] },
      { name: 'გადაბმულობა და კოლოფი', children: ['გადაბმულობის ნაკრები', 'მაქოვანა', 'გადაბმულობის ცილინდრი', 'კოლოფის ბალიში'] },
      { name: 'გამონაბოლქვი', children: ['მაყუჩი', 'კატალიზატორი', 'ლამბდა ზონდი', 'გამონაბოლქვის მილი', 'გოფრი'] },
      { name: 'ელექტრო', children: ['აკუმულატორი', 'გენერატორი', 'სტარტერი', 'სენსორი', 'რელე და დამცავი', 'გაყვანილობა', 'საწმენდის ძრავა'] },
      { name: 'განათება', children: ['წინა ფარი', 'უკანა ფარი', 'ნისლის ფარი', 'ნათურა', 'LED', 'მოსახვევის მაჩვენებელი'] },
      { name: 'ძარა', children: ['ბამპერი', 'ფრთა', 'კაპოტი', 'კარი', 'სარკე', 'გისოსი', 'საბარგულის სახურავი', 'ზღურბლი'] },
      { name: 'მინა და საწმენდი', children: ['საქარე მინა', 'გვერდითი მინა', 'უკანა მინა', 'საწმენდი (ჯაგრისი)'] },
      { name: 'საბურავი და დისკი', children: ['საბურავი ზაფხულის', 'საბურავი ზამთრის', 'დისკი', 'ბოლტი და ქანჩი', 'ბალანსირების ტვირთი', 'დისკის ხუფი'] },
      { name: 'სალონი', children: ['სავარძლის ჩეხოლი', 'ხალიჩა', 'საჭის ჩეხოლი', 'არომატიზატორი', 'ტელეფონის სამაგრი', 'მზის დამცავი'] },
      { name: 'ავტოქიმია და მოვლა', children: ['პოლიროლი', 'ავტოშამპუნი', 'WD-40 და შემზეთი', 'ანტიკოროზიული', 'სალონის საწმენდი'] },
      { name: 'აქსესუარი და ინსტრუმენტი', children: ['ბუქსირის ბაგირი', 'დამტენი სადენი', 'კომპრესორი (ტუმბო)', 'ამწე (დომკრატი)', 'ცეცხლმაქრი', 'სამედიცინო ყუთი', 'გასაღებების ნაკრები'] },
      { name: 'მეორადი ნაწილი', children: ['ძრავი', 'გადაცემათა კოლოფი', 'კარი', 'კაპოტი', 'ბამპერი', 'ფარი', 'სარკე', 'რადიატორი', 'საკიდარის ნაწილი', 'სალონის ნაწილი'] },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    // A pet shop is walked by ANIMAL first — a buyer comes in for the dog or
    // the cat, not for „food" — and within the animal by what the thing is:
    // food, care, gear. Food splits dry / wet / treats because that is how
    // it is shelved and how it is asked for. Brands (Royal Canin, Whiskas)
    // are left out on purpose: the same brand sits on the dog shelf and the
    // cat shelf, and a brand-per-category tree puts one product in each.
    // Three pet shops are on the way (roadmap); asked for 2026-09-14.
    key: 'petshop',
    name: 'ზოო მაღაზია',
    hint: 'ძაღლი, კატა, ფრინველი, თევზი, მღრღნელი — საკვები, მოვლა, აქსესუარი',
    emoji: '🐾',
    groups: [
      { name: 'ძაღლის საკვები', children: ['მშრალი საკვები', 'სველი საკვები', 'ლეკვის საკვები', 'წამახალისებელი', 'ძვალი და საღეჭი'] },
      { name: 'კატის საკვები', children: ['მშრალი საკვები', 'სველი საკვები', 'კნუტის საკვები', 'წამახალისებელი'] },
      { name: 'ძაღლის მოვლა', children: ['შამპუნი', 'სავარცხელი და ჯაგრისი', 'ბრჭყალის საკვნეტი', 'პირის ღრუს მოვლა', 'საფენი'] },
      { name: 'კატის მოვლა', children: ['ქვიშა', 'ქვიშის ყუთი და ნიჩაბი', 'შამპუნი', 'სავარცხელი', 'საფხეკი'] },
      { name: 'ძაღლის აქსესუარი', children: ['საყელო', 'საბელი (გასასეირნებელი)', 'შლეიკა', 'ალიკაპი', 'ტანსაცმელი', 'სათამაშო', 'საწოლი'] },
      { name: 'კატის აქსესუარი', children: ['საყელო', 'სათამაშო', 'საწოლი და სახლი', 'გადასაყვანი ჩანთა'] },
      { name: 'ჭურჭელი', children: ['თასი', 'ავტომატური საკვებური', 'წყლის შადრევანი'] },
      { name: 'ფრინველი', children: ['თუთიყუშის საკვები', 'კანარის საკვები', 'გალია', 'გალიის აქსესუარი', 'ვიტამინი'] },
      { name: 'თევზი და აკვარიუმი', children: ['თევზის საკვები', 'აკვარიუმი', 'ფილტრი და ტუმბო', 'გამათბობელი', 'განათება', 'გრუნტი და დეკორი', 'წყლის საშუალება'] },
      { name: 'მღრღნელი და ბოცვერი', children: ['საკვები', 'გალია', 'თივა და საფენი', 'სათამაშო და ბორბალი'] },
      { name: 'ქვეწარმავალი', children: ['საკვები', 'ტერარიუმი', 'განათება და გათბობა'] },
      { name: 'ვეტერინარია', children: ['ჭიის საწინააღმდეგო', 'რწყილისა და ტკიპის საწინააღმდეგო', 'ვიტამინი და დანამატი', 'ჭრილობის მოვლა', 'პრობიოტიკი'] },
      { name: 'ტრანსპორტირება', children: ['გადასაყვანი ყუთი', 'ჩანთა', 'მანქანის საფენი'] },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    key: 'accessories',
    name: 'ტელეფონის აქსესუარები',
    hint: 'დამტენი, კაბელი, ყურსასმენი, ჩეხოლი',
    emoji: '📱',
    groups: [
      { name: 'დამტენი', children: ['კედლის დამტენი', 'ავტო დამტენი', 'უსადენო'] },
      { name: 'კაბელი', children: ['Type-C', 'Lightning', 'Micro USB'] },
      { name: 'ყურსასმენი', children: ['სადენიანი', 'უსადენო'] },
      { name: 'Power Bank' },
      { name: 'ჩეხოლი' },
      { name: 'დამცავი შუშა' },
      { name: 'SIM ბარათი' },
      { name: 'მეხსიერების ბარათი' },
      { name: 'სათადარიგო ნაწილები' },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    key: 'coffee',
    name: 'ყავა / კაფე',
    hint: 'ცხელი და ცივი სასმელი, ნამცხვარი, ერთჯერადი',
    emoji: '☕',
    groups: [
      { name: 'ცხელი სასმელი', children: ['ესპრესო', 'ამერიკანო', 'კაპუჩინო', 'ლატე', 'ჩაი', 'ცხელი შოკოლადი'] },
      { name: 'ცივი სასმელი', children: ['ყინულოვანი ყავა', 'ლიმონათი', 'წვენი', 'წყალი'] },
      { name: 'ნამცხვარი და დესერტი', children: ['კრუასანი', 'ნამცხვარი', 'ორცხობილა'] },
      { name: 'სენდვიჩი' },
      { name: 'ერთჯერადი', children: ['ჭიქა', 'თავსახური', 'ჩხირი', 'ხელსახოცი'] },
      { name: 'ნედლეული', children: ['ყავის მარცვალი', 'რძე', 'სიროფი', 'შაქარი'] },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    // A stationery chain (3 branches) is coming (roadmap, 2026-09). Shelves
    // are what a school-year customer asks for — „საწერი", „რვეული", „სასკოლო" —
    // and books sit here too because every Georgian stationery shop sells them.
    key: 'stationery',
    name: 'საკანცელარიო / წიგნები',
    hint: 'საწერი, რვეული, ქაღალდი, სასკოლო, სახატავი, წიგნები',
    emoji: '✏️',
    groups: [
      { name: 'საწერი', children: ['კალამი', 'ფანქარი', 'მარკერი', 'ფლომასტერი', 'საშლელი', 'სათლელი'] },
      { name: 'რვეული და ბლოკნოტი', children: ['რვეული', 'ბლოკნოტი', 'დღიური', 'ესკიზების რვეული'] },
      { name: 'ქაღალდი', children: ['A4 ქაღალდი', 'ფერადი ქაღალდი', 'სტიკერი', 'კონვერტი'] },
      { name: 'საქაღალდე', children: ['საქაღალდე', 'ფაილი', 'რეგისტრატორი', 'ორგანაიზერი'] },
      { name: 'სასკოლო', children: ['ჩანთა', 'პენალი', 'სახაზავი', 'ფარგალი', 'ტრანსპორტირი'] },
      { name: 'სახატავი', children: ['ფერადი ფანქარი', 'აკვარელი', 'გუაში', 'ფუნჯი', 'პლასტილინი', 'ალბომი'] },
      { name: 'საოფისე', children: ['სტეპლერი', 'სკოჩი', 'წებო', 'მაკრატელი', 'კალკულატორი', 'სამაგრი'] },
      { name: 'წიგნები', children: ['სახელმძღვანელო', 'მხატვრული', 'საბავშვო', 'ლექსიკონი'] },
      { name: 'სასაჩუქრე', children: ['შესაფუთი ქაღალდი', 'ბაფთა', 'მისალოცი ბარათი', 'სასაჩუქრე პარკი'] },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    // Cosmetics is walked by WHAT IT IS FOR (face, hair, body), then by
    // product type; brands come as sub-shelves only where the shop is a
    // brand counter — the owner adds those.
    key: 'cosmetics',
    name: 'კოსმეტიკა / პარფიუმერია',
    hint: 'სახის, თმის, სხეულის მოვლა, დეკორატიული, სუნამო',
    emoji: '💄',
    groups: [
      { name: 'სახის მოვლა', children: ['კრემი', 'შრატი', 'ნიღაბი', 'ტონიკი', 'დასაბანი', 'მზისგან დამცავი'] },
      { name: 'დეკორატიული კოსმეტიკა', children: ['ტონალური კრემი', 'პუდრა', 'ტუში', 'ტუჩსაცხი', 'ლაინერი', 'ჩრდილები', 'ღაწვების საღებავი'] },
      { name: 'თმის მოვლა', children: ['შამპუნი', 'კონდიციონერი', 'თმის ნიღაბი', 'ლაქი და მუსი', 'თმის საღებავი'] },
      { name: 'სხეულის მოვლა', children: ['ლოსიონი', 'სკრაბი', 'დეზოდორანტი', 'შხაპის გელი', 'საპონი'] },
      { name: 'პარფიუმერია', children: ['ქალის', 'მამაკაცის', 'უნისექსი'] },
      { name: 'ფრჩხილის მოვლა', children: ['ფრჩხილის ლაქი', 'ლაქის მოსაშორებელი', 'ფრჩხილის ქლიბი'] },
      { name: 'აქსესუარები', children: ['ფუნჯი', 'ღრუბელი', 'სავარცხელი', 'პინცეტი', 'სარკე'] },
      { name: 'მამაკაცისთვის', children: ['საპარსი', 'საპარსი ქაფი', 'პარსვის შემდგომი'] },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    // Clothes are walked by WHO wears them, then by what it is; shoes and
    // accessories are their own shelves. Sizes and colours are NOT
    // categories — they are a model's sizes (112, lib/variants.ts).
    key: 'clothing',
    name: 'ტანსაცმელი / ფეხსაცმელი',
    hint: 'ქალის, მამაკაცის, საბავშვო, ფეხსაცმელი, აქსესუარები',
    emoji: '👕',
    groups: [
      { name: 'ქალის ტანსაცმელი', children: ['კაბა', 'ბლუზა', 'შარვალი', 'ქვედაბოლო', 'ჟაკეტი', 'სვიტერი'] },
      { name: 'მამაკაცის ტანსაცმელი', children: ['პერანგი', 'მაისური', 'შარვალი', 'ჯინსი', 'პიჯაკი', 'სვიტერი'] },
      { name: 'საბავშვო', children: ['გოგონები', 'ბიჭები', 'ჩვილები'] },
      { name: 'ზედა ტანსაცმელი', children: ['ქურთუკი', 'პალტო', 'ჟილეტი', 'საწვიმარი'] },
      // „უნისექსი" — the owner, 2026-09-28, on PUMA's „Suede Classic Sneakers Unisex".
      { name: 'ფეხსაცმელი', children: ['ქალის', 'მამაკაცის', 'უნისექსი', 'საბავშვო', 'სპორტული', 'ჩუსტი'] },
      { name: 'საცვალი და წინდა', children: ['ქალის საცვალი', 'მამაკაცის საცვალი', 'წინდა', 'კოლგოტი'] },
      { name: 'სპორტული', children: ['სპორტული ფორმა', 'ლეგინსი', 'სპორტული მაისური'] },
      { name: 'აქსესუარები', children: ['ჩანთა', 'ქამარი', 'ქუდი', 'შარფი', 'სათვალე', 'ხელთათმანი'] },
      { name: 'სხვადასხვა' },
    ],
  },
  {
    // A hardware shop is walked by JOB: tools, fasteners, paint, electrics,
    // plumbing. Words are the shop's own Georgian — „კუთხსახეხი", „ხრახნი",
    // „გორგოლაჭი" — not the market's Russian loans, because the owner has
    // corrected borrowed words before (ძარა, not კუზოვი).
    key: 'hardware',
    name: 'სამშენებლო / ხელსაწყოები',
    hint: 'ხელსაწყოები, სამაგრები, საღებავი, ელექტროობა, სანტექნიკა',
    emoji: '🔨',
    groups: [
      { name: 'ხელის ხელსაწყოები', children: ['ჩაქუჩი', 'სახრახნისი', 'ქანჩის გასაღები', 'მარწუხი', 'ხერხი', 'სახაზავი და დონე'] },
      { name: 'ელექტრო ხელსაწყოები', children: ['დრელი', 'ელექტრო სახრახნისი', 'კუთხსახეხი', 'ელექტრო ხერხი', 'ბურღი'] },
      { name: 'სამაგრები', children: ['ჭანჭიკი და ქანჩი', 'ლურსმანი', 'ხრახნი', 'დუბელი', 'ანკერი'] },
      { name: 'საღებავი და ლაქი', children: ['საღებავი', 'გრუნტი', 'ლაქი', 'ფუნჯი', 'გორგოლაჭი', 'გამხსნელი'] },
      { name: 'ელექტროობა', children: ['კაბელი', 'ჩამრთველი', 'როზეტი', 'ნათურა', 'ავტომატური ამომრთველი', 'დამაგრძელებელი'] },
      { name: 'სანტექნიკა', children: ['ონკანი', 'მილი და ფიტინგი', 'შლანგი', 'სიფონი', 'ვენტილი'] },
      { name: 'სამშენებლო მასალები', children: ['ცემენტი', 'წებო-ცემენტი', 'ფითხი', 'ბათქაში', 'თაბაშირ-მუყაო'] },
      { name: 'საიზოლაციო', children: ['სილიკონი', 'სამონტაჟო ქაფი', 'იზოლენტა', 'საიზოლაციო ლენტი'] },
      { name: 'უსაფრთხოება', children: ['ხელთათმანი', 'დამცავი სათვალე', 'რესპირატორი', 'ჩაფხუტი'] },
      { name: 'სხვადასხვა' },
    ],
  },
]

/**
 * Step 3b of the owner's plan (2026-09-26): a template is a shop PROFILE, not
 * only a category tree. `scale` turns the scale on when the template is
 * applied; `tips` are what a shop of that kind needs first — each one about a
 * feature that EXISTS, in the words the app uses for it („თანმხლები (კომბო)",
 * „დაშლა", the expiry field). A tip about something not built yet is a promise
 * the shop will try the next morning. `unit` is what a new product starts
 * with in the product card.
 */
export type TemplateProfile = {
  scale?: boolean
  /** 1.40 — the unit a NEW product starts with (the owner can still pick another). */
  unit?: ProductUnit
  /** 1.61 — a NEW product starts as „ვადიანი" (its stock comes in dated batches). */
  expiry?: boolean
  tips: string[]
}

export const TEMPLATE_PROFILES: Record<string, TemplateProfile> = {
  grocery: { expiry: true, tips: [
    'ნისია — ჩაწერე კლიენტი; ვის რამდენი მართებს, დილის ანგარიშშიც ჩანს.',
    'ყველს ან ძეხვს წონით ყიდი? ჩართე სასწორი: პარამეტრები → ელექტრო სასწორი.',
  ] },
  produce: { expiry: true, scale: true, unit: 'kg', tips: [
    'სასწორის ეტიკეტი სკანერით პირდაპირ კალათაში ემატება — წონით ან ფასით.',
    'პროდუქტს მიუთითე ერთეული „კგ" და PLU კოდი — სასწორი მას ამ კოდით ცნობს.',
    'ელექტრო სასწორი არ გაქვს? სალაროში პროდუქტზე დააჭირე — წონას ან თანხას თვითონ გკითხავს.',
    'ტუშს თვითონ ჭრი? „დაშლა": ტუში → რბილობი, ნეკნი, დაკეპილი ხორცი — თვითღირებულება ნაწილებზე გასაყიდი ფასით ნაწილდება.',
  ] },
  poultry: { expiry: true, unit: 'kg', tips: [
    'სალაროში პროდუქტზე დააჭირე და ჩაწერე წონა — ან თანხა: „₾10-ის ფრთები". ელექტრო სასწორი საჭირო არ არის.',
    'ქათამი წონას კარგავს? პარტიის ბოლოს დანაკლისი ჩამოწერე — ნაშთიც გასწორდება და მოგებაც. ინვენტარიზაცია მხოლოდ ნაშთს ასწორებს.',
    'კვერცხი კასეტით შემოდის და ცალობით იყიდება? გამოიყენე „დაშლა": 1 კასეტა = 30 ცალი.',
  ] },
  kiosk: { expiry: true, tips: [
    'ყავის ჭიქა და თავსახური ყავის „თანმხლებ (კომბო)" პროდუქტად დაამატე — მარაგი თვითონ ჩამოიწერება.',
    'სიგარეტი ბლოკით შემოდის და კოლოფით იყიდება? გამოიყენე „დაშლა".',
  ] },
  household: { tips: [
    'ბევრი პროდუქტი გაქვს? Excel-ით შემოიტანე: საწყობი → Excel → იმპორტი.',
    'ნაკრები ცალობითაც იყიდება? „დაშლა": 1 ნაკრები → 6 თეფში.',
  ] },
  autoparts: { tips: [
    'RS.GE-ის ზედნადებიდან მიღებისას მომწოდებლის კოდი პროდუქტს ებმება — შემდეგ ზედნადებზე აპი თვითონ იცნობს.',
    'პროდუქტზე ჩაწერე, რომელ მანქანას ერგება და მისი OEM ნომრები — სალაროში „astra 2008"-ით ან ნომრით მოიძებნება, გათავებულზე კი ანალოგს შემოგთავაზებს.',
  ] },
  petshop: { expiry: true, tips: [
    'საკვები ტომრით შემოდის და კილოგრამით იყიდება? გამოიყენე „დაშლა" და ჩართე სასწორი.',
  ] },
  accessories: { tips: [
    'პროდუქტის ფოტო სალაროში ჩანს — მოლარე სწრაფად პოულობს.',
    'შტრიხკოდის ეტიკეტი საწყობიდან იბეჭდება: „ეტიკეტები".',
    'შუშის დაკვრა ან სხვა სამუშაო — დაამატე მომსახურებად: მარაგს არ ითვლის.',
    'ქეისზე ან მინაზე ჩაწერე ტელეფონის მოდელი („ერგება") — სალაროში „iphone 15"-ით მოიძებნება.',
  ] },
  coffee: { tips: [
    'რძე, სიროფი, ჭიქა — სასმელის „თანმხლებ (კომბო)" პროდუქტად დაამატე; სალარო თვითონ შემოგთავაზებს.',
    'ჭიქა ₾0-ად შეიძლება — თვითღირებულება მოგებაში მაინც ჩაითვლება.',
  ] },
  stationery: { tips: [
    'ბევრი წვრილმანი გაქვს? Excel-ით შემოიტანე: საწყობი → Excel → იმპორტი.',
    'ასლი, ბეჭდვა, ლამინირება — დაამატე მომსახურებად: სალარო ყიდის, მარაგს არ ითვლის.',
  ] },
  cosmetics: { expiry: true, tips: [
    'პროდუქტს ვადა მიუთითე — ვადის გასვლამდე 14 დღით ადრე გაგაფრთხილებთ.',
  ] },
  clothing: { tips: [
    'ზომები და ფერები — „მოდელად": საწყობი → „მოდელი". თითო ზომას თავისი მარაგი და შტრიხკოდი აქვს, სალაროში კი მოდელი ერთ ბარათად ჩანს.',
    'მომწოდებლის შტრიხკოდი არ აქვს? აპი მაღაზიის კოდს თვითონ შექმნის და ეტიკეტს დაბეჭდავს.',
  ] },
  hardware: { tips: [
    'მეტრით ან კილოგრამით ყიდი? რაოდენობა წილადითაც ჩაიწერება: 2.5 მ.',
  ] },
}

export const templateProfile = (key: string): TemplateProfile => TEMPLATE_PROFILES[key] ?? { tips: [] }

/** A Georgian category word → its dictionary key. */
const WORD_KEY = new Map<string, Key>(
  Object.entries(kaWords).filter(([k]) => k.startsWith('cat.')).map(([k, v]) => [v, k as Key]),
)

/** The template in `tr`'s language — what the picker shows and what applying it creates. */
export function templateIn(t: CategoryTemplate, tr: T): CategoryTemplate {
  const word = (w: string) => { const k = WORD_KEY.get(w); return k ? tr(k) : w }
  return {
    ...t,
    name: tr(`tpl.${t.key}.name` as Key),
    hint: tr(`tpl.${t.key}.hint` as Key),
    groups: t.groups.map((g) => ({ name: word(g.name), children: g.children?.map(word) })),
  }
}

/** The profile's tips in `tr`'s language. */
export function profileIn(key: string, tr: T): TemplateProfile {
  const p = templateProfile(key)
  return { ...p, tips: p.tips.map((_, i) => tr(`tpl.${key}.tip${i + 1}` as Key)) }
}

/** Rows a template would create — parents plus their children. */
export function templateSize(t: CategoryTemplate): { parents: number; children: number } {
  return {
    parents: t.groups.length,
    children: t.groups.reduce((n, g) => n + (g.children?.length ?? 0), 0),
  }
}
